import { useCallback, useEffect, useRef } from 'react';
import { useChain, useSpringRef } from 'react-spring';
import Scale from '../Animations/Scale';
import Slide from '../Animations/Slide';
import styles from '../../styles/Circle.module.scss';

const slideConfig = {
  mass: 30,
  tension: 300,
  friction: 63,
  clamp: false,
  precision: 0.01,
  velocity: 0,
  damping: 0.5,
  frequency: 0.7,
  bounce: 0.45
};

export default function Circle() {
  const circleAnimeRef = useSpringRef();
  const textAnimeRef = useSpringRef();
  const underlineAnimeRef = useSpringRef();

  useChain([circleAnimeRef, textAnimeRef, underlineAnimeRef], [0, 0.2]);

  const divToSlideLeft = useRef<HTMLDivElement>(null);
  const divToSlideRight = useRef<HTMLDivElement>(null);
  const divToScale = useRef<HTMLDivElement>(null);
  const prevScrollY = useRef(window.scrollY);

  const handleScroll = useCallback(() => {
    if (
      !divToSlideLeft.current ||
      !divToSlideRight.current ||
      !divToScale.current
    )
      return;

    const currentScrollY = window.scrollY;

    function slide(translateX: 'left' | 'right', el: HTMLElement) {
      if (currentScrollY <= 0) {
        el.style.transform = 'translateX(0px)';
        return;
      }

      const style = window.getComputedStyle(el);
      const matrix = new WebKitCSSMatrix(style.transform);
      const prevX = matrix.m41;

      let transformVal = 0;
      if (translateX === 'left') {
        const newOffset = prevX + prevScrollY.current - currentScrollY;
        if (newOffset < 0) transformVal = newOffset;
      } else if (translateX === 'right') {
        const newOffset = currentScrollY - prevScrollY.current + prevX;
        if (newOffset > 0) transformVal = newOffset;
      }
      el.style.transform = `translateX(${transformVal}px)`;
    }

    function scale(el: HTMLElement) {
      if (currentScrollY <= 0) {
        el.style.transform = 'scale(1)';
        return;
      }

      const scaleX = el.getBoundingClientRect().width / el.offsetWidth;
      let transformVal = 1;

      const newOffset =
        scaleX - (currentScrollY - prevScrollY.current) / 300;

      if (newOffset < 0) transformVal = 0;
      else if (
        prevScrollY.current > currentScrollY &&
        currentScrollY > 342
      )
        transformVal = 0;
      else if (newOffset < 1) transformVal = newOffset;

      el.style.transform = `scale(${transformVal})`;
    }

    slide('left', divToSlideLeft.current);
    slide('right', divToSlideRight.current);
    scale(divToScale.current);

    prevScrollY.current = currentScrollY;
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);
  return (
    <>
      <Scale
        elRef={divToScale}
        className={styles.circle + ' shootable_el boundary'}
        animationRef={circleAnimeRef}
      />

      <div className={`${styles.text_block} ${styles.top}`}>
        <div className={styles.ctn}>
          <Slide
            className="shootable_el boundary"
            start={{ transform: 'translateX(100vw)' }}
            end={{
              transform: 'translate(0)'
            }}
            animationRef={textAnimeRef}
            config={slideConfig}
            tag="h2"
          >
            Alex Liang
          </Slide>
          <Slide
            start={{ transform: 'translateX(100vw)' }}
            end={{
              transform: 'translate(0)'
            }}
            elRef={divToSlideLeft}
            animationRef={underlineAnimeRef}
            className={styles.underline + ' shootable_el boundary'}
            config={{
              mass: 50,
              tension: 2000,
              friction: 300
            }}
          />
        </div>
      </div>

      <div className={`${styles.text_block} ${styles.bottom}`}>
        <div className={styles.ctn}>
          <Slide
            className="shootable_el boundary"
            start={{ transform: 'translateX(-100vw)' }}
            end={{
              transform: 'translate(0)'
            }}
            animationRef={textAnimeRef}
            config={slideConfig}
            tag="h2"
          >
            web developer
          </Slide>
          <Slide
            start={{ transform: 'translateX(-100vw)' }}
            end={{
              transform: 'translate(0)'
            }}
            elRef={divToSlideRight}
            animationRef={underlineAnimeRef}
            className={styles.underline + ' shootable_el boundary'}
            config={{
              mass: 50,
              tension: 3000,
              friction: 400
            }}
          />
        </div>
      </div>
    </>
  );
}
